<template>
  <VInput v-bind="inputProps" ref="input" v-model="modelValue" type="search" allow-empty data-component-name="VSearch">
    <template v-if="icon" #[iconPosition]>
      <VIcon :name="icon" color="neutral/450" />
    </template>
    <template v-if="filteredSuggestions.length > 0" #teleport>
      <VTeleport v-if="menuOpen" to="body">
        <div
          class="modern-color-theme font-poppins fixed z-[1001] mt-1 rounded-md bg-neutral-100 shadow-sm ring-1 ring-inset w-full ring-neutral-250 flex flex-col p-1 max-h-96"
          :style="{
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.10), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            ...position
          }"
        >
          <VButton v-for="(suggestion) in filteredSuggestions" :key="suggestion.value" raw wide class="gap-3" :container-props="{ class: 'py-2 px-3 rounded-lg hover:bg-primary-150' }" @click="acceptSuggestion(suggestion)">
            <VIcon v-if="suggestion.icon" size="sm" color="neutral/500" :name="suggestion.icon" />
            <div class="flex flex-col text-left">
              <div v-safe-html="suggestion.labelHtml" class="text-sm leading-5 font-medium text-neutral-750" />
              <div v-if="suggestion.description" class="text-xs leading-4 font-normal text-neutral-500">{{ suggestion.description }}</div>
            </div>
          </VButton>
        </div>
      </VTeleport>
    </template>
  </VInput>
</template>
<script lang="ts" setup>
import VInput from './VInput.vue';
import VIcon from '../labels/VIcon.vue';
import type { BufferingProps, LengthRestrictionProps, PlaceholderProps, SharedProps, ValidationProps } from './helpers/types';
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';
import { useFallthroughProps } from './helpers/fallthrough';
import type { Icon } from '@icons/index';
import { V } from '@component-utils/types';
import { getDOMRect, useAnimationFramePosition } from '@component-utils/position';
import VTeleport from '@component-library/utilities/VTeleport.vue';
import { getFilteredCollection } from '@component-utils/search';
import VButton from '@component-library/buttons/VButton.vue';

defineOptions({
  name: 'VSearch',
  inheritAttrs: false
})

const props = withDefaults(
  defineProps<SharedProps & ValidationProps<string> & LengthRestrictionProps & PlaceholderProps & BufferingProps & {
    focus?: boolean
    centered?: boolean
    icon?: Icon | null
    iconPosition?: 'prefix' | 'suffix'
    suggestions?: V.Search.Suggestion[]
  }>(),
  {
    iconPosition: 'prefix',
    icon: undefined,
    suggestions: () => []
  }
)

const emit = defineEmits<{
  acceptSuggestion: [value: string]
}>()

const acceptSuggestion = async (suggestion: V.Search.Suggestion) => {
  emit('acceptSuggestion', suggestion.value)

  modelValue.value = suggestion.label

  await nextTick()

  menuOpen.value = false
}

const icon = computed(() => typeof props.icon === 'string' ? props.icon : props.icon === null ? false : 'Solid/search')
const iconPosition = computed(() => props.iconPosition)

const inputProps = useFallthroughProps(props)

const modelValue = defineModel<undefined | null | string>({ required: true })

const inputElement = useTemplateRef('input')

const menuOpen = ref(false)

watch(modelValue, (value) => {
  menuOpen.value = !!value
})

const filteredSuggestions = computed(() => {
  const value = modelValue.value
  if (!value) return []

  return getFilteredCollection(props.suggestions, value, ['label'], { fuzzy: false }).slice(0, 5).map((suggestion) => ({
    ...suggestion,
    labelHtml: highlightLabel(suggestion.label, value)
  }))
})

const position = useAnimationFramePosition(
  menuOpen,
  () => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const rect = getDOMRect(inputElement.value?.internalComponent)

    const menuHeight = 300
    const menuWidth = Math.max(rect.width, 300)

    const windowHeight = window.innerHeight

    const verticalPosition = rect.bottom + menuHeight > windowHeight ? {
      bottom: `${windowHeight - rect.top}px`
    } : {
      top: `${rect.bottom}px`
    }

    const basePosition = {
      left: `${rect.left}px`,
      width: `${menuWidth}px`
    }

    return Object.assign(verticalPosition, basePosition)
  }
)

const highlightLabel = (label: string, highlightedText: string) => {
  const array = new RegExp(`^(.*)(${RegExp.escape(highlightedText.trim())})(.*)$`, 'iu').exec(label)
  if (!array) return label

  const [, pre, match, post] = array
  return `${pre}<span class="text-primary-500">${match}</span>${post}`
}

defineExpose({
  focus: () => {
    inputElement.value?.focus()
  },
  get isValid () {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return inputElement.value?.isValid ?? false
  },
  get isEmpty () {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return inputElement.value?.isEmpty ?? true
  }
})
</script>
